import { createContext, useContext, useMemo, type ReactNode } from "react";
import { ToastProvider as RdsToastProvider, useToast as useRdsToast } from "@/deps/ui-library";

/**
 * Somewhere for a screen to say what just happened, shown by RDS's toaster (ADR-0054).
 *
 * A toast reports the *outcome* of something the person asked for — a sync finished, this many
 * documents arrived. It is not where a refusal goes: a refusal belongs beside the control that was
 * refused, where the person is already looking, and a toast that disappears after a few seconds is
 * a poor place to put a reason somebody needs to act on.
 */
export type ToastScheme = "default" | "success" | "info" | "warning" | "danger";

interface ToastRequest {
  title: string;
  description?: string;
  scheme?: ToastScheme;
}

const ToastContext = createContext<((toast: ToastRequest) => void) | null>(null);

/** How long a toast stays. Long enough to read a sentence, short enough not to sit in the way. */
const DISMISS_AFTER_MS = 6000;

const TYPE = {
  default: "default",
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
} as const;

/** Bridges the product's small toast API onto the RDS toast manager. */
function Bridge({ children }: { children: ReactNode }) {
  const manager = useRdsToast();
  const show = useMemo(
    () => (toast: ToastRequest) =>
      manager.toast({
        title: toast.title,
        description: toast.description,
        type: TYPE[toast.scheme ?? "default"],
        duration: DISMISS_AFTER_MS,
      }),
    [manager],
  );
  return <ToastContext.Provider value={show}>{children}</ToastContext.Provider>;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <RdsToastProvider>
      <Bridge>{children}</Bridge>
    </RdsToastProvider>
  );
}

/**
 * Raises a toast.
 *
 * Returns a no-op outside a provider rather than throwing: a component rendered in a test or a
 * preview should not fail because nobody mounted the host, and a missing toast is not a defect
 * worth crashing a screen over.
 */
export function useToast(): (toast: ToastRequest) => void {
  return useContext(ToastContext) ?? noop;
}

const noop = () => {};
